import React, { FC, useState } from "react";
import { TabContainer, Container, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.css";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Image from "react-bootstrap/Image";
import ButtonWrapper from "./ButtonWrapper";
import Reservations from "./Reservations";
import Details from "./Details";
import Vehicles from "./Vehicles";
import Reports from "./Reports";
import { useAuth } from "../auth/authContext";
import { useProfile, useVehicles } from "../api/hooks";
import "../custom.css";
import { ProfileComplete } from "./ProfileComplete";
import { useParams, useNavigate } from "react-router-dom";
import QRCodePanel from "./QrCode";
import Locations from "./Locations";

export const Layout: FC = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { logout, sendEmailVerification, currentUser } = useAuth();
  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const { data: vehicles, isLoading: isLoadingVehicles } = useVehicles();
  const [verificationSent, setVerificationSent] = useState(false);

  const verify = async () => {
    await sendEmailVerification();
    setVerificationSent(true);
  };

  const isLoading = isLoadingProfile || isLoadingVehicles;

  if (isLoading) {
    return (
      <div className="d-flex flex-column min-vh-100">
        {/* Header */}
        <Container className="nav-justified g-0 flex-column flex-md-row px-5 py-3">
          <Row className="h-100 align-items-center px-2">
            <Col>
              <Image src={"/images/sharepark-logo.svg"} width="180px" />
            </Col>
          </Row>
        </Container>
        
        {/* Loading Content */}
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1" style={{ backgroundColor: "#f8f9fa" }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="text-muted">Loading your account...</h5>
            <p className="text-muted small">Please wait while we grab your details</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column overflow-hidden min-vh-100">
      <Container className="nav-justified g-0 flex-column flex-md-row px-5 py-3">
        <Row className="h-100 align-items-center px-2">
          <Col>
            <Image src={"/images/sharepark-logo.svg"} width="180px" />
          </Col>
          <Col className="text-end">
            <ButtonWrapper buttonText="Log Out" onClick={logout} />
          </Col>
        </Row>
      </Container>

      {currentUser?.emailVerified && profile?.email != null ? (
        <>
          <ProfileComplete profile={profile} vehicles={vehicles || []} />

          <Tabs
            activeKey={tab}
            defaultActiveKey="qr-code"
            onSelect={(nextTab) => navigate("/" + nextTab)}
            className="container nav-justified tabs g-0 flex-column flex-lg-row px-5"
          >
            <Tab
              eventKey="qr-code"
              title="QR&nbsp;Code"
              className="flex-grow-1"
            >
              <TabContainer>
                {tab === "qr-code" && <QRCodePanel />}
              </TabContainer>
            </Tab>
            <Tab
              eventKey="reservations"
              title="Reservations"
              className="flex-grow-1"
            >
              <TabContainer>
                {tab === "reservations" && <Reservations />}
              </TabContainer>
            </Tab>
            <Tab eventKey="locations" title="Locations" className="flex-grow-1">
              <TabContainer>
                {tab === "locations" && <Locations />}
              </TabContainer>
            </Tab>
            <Tab eventKey="details" title="Details" className="flex-grow-1">
              <TabContainer>{tab === "details" && <Details />}</TabContainer>
            </Tab>
            <Tab eventKey="vehicles" title="Vehicles" className="flex-grow-1">
              <TabContainer>{tab === "vehicles" && <Vehicles />}</TabContainer>
            </Tab>
            <Tab eventKey="reports" title="Reports" className="flex-grow-1">
              <TabContainer>{tab === "reports" && <Reports />}</TabContainer>
            </Tab>
          </Tabs>
        </>
      ) : (
        <div
          style={{ flexGrow: 1, backgroundColor: "#f2f2f2" }}
          className="d-flex justify-content-center align-items-center"
        >
          {!currentUser?.emailVerified && ( // only show if firebase userprofile is unverified
            <div className="text-center">
              {verificationSent ? (
                <p>Check your email and click on the link provided</p>
              ) : (
                <>
                  <h6>
                    Please verify your email address:{" "}
                    <strong>{currentUser?.email}</strong>
                  </h6>
                  <div className="mt-3">
                    <button className="orangeButton" onClick={verify}>
                      Send Email Verification
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="footer">
        <Container className="mt-auto nav-justified g-0 flex-column flex-md-row px-5 py-2">
          <Row>
            <Col xs="auto">
              <Image src={"/images/shareparkSquareLogo.svg"} width="38px" />
            </Col>
            <Col>
              <div>
                <a
                  style={{ color: "#fe9015" }}
                  href="mailto:preisner@parkagility.com"
                >
                  Contact Us
                </a>
              </div>
              <div>Account Support</div>
              <div>
                <a
                  rel="noreferrer"
                  target="_blank"
                  href="https://assets.website-files.com/5dce688264679143b99e2794/5f4316c56896716e3488e558_SharePark%20Service%20-%20Terms%20of%20Use%20Aug%202020.pdf"
                >
                  Terms of Service
                </a>
              </div>
              <div>Copyright © {new Date().getFullYear()} Share Park</div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};
